import { ApolloLink, Observable, from } from "@apollo/client/core";
import { onError } from "@apollo/client/link/error";
import { ref } from "vue";

import { getLogger } from "@/utils/logger";

/**
 * A tab whose bundle predates a release, asking for fields the new schema no
 * longer has (#6497).
 *
 * The backend answers a query that does not validate against its schema with
 * HTTP 400 before any resolver runs (`GraphQLController`, the
 * `QueryAnalysisError` arm). The tab cannot recover from that: every retry
 * sends the same document and gets the same refusal, and the meeting store
 * retries on every signal. So the first such answer latches this flag, `App.vue`
 * asks the person to reload, and no later operation reaches the network.
 *
 * One-way, like `maintenanceActive`: only a reload clears it.
 */
export const schemaMismatchDetected = ref(false);

/** What a caller sees in place of a response once the latch is set. */
export const SCHEMA_MISMATCH_MESSAGE = "A new version of ToastHost is available. Reload the page to keep working.";

const logger = getLogger("GraphQLSchemaMismatch");

/**
 * Sangria's message for a variable value the schema cannot coerce. That error
 * is a `QueryAnalysisError` too, and its body has the same shape as a
 * validation error. It can come from a current bundle sending a bad value, so
 * it is not evidence of a stale one.
 */
const VARIABLE_COERCION_MESSAGE = /^Variable '\$[^']*' expected value of type /;

/**
 * True when every entry of a GraphQL error list is a validation violation:
 * it points at the document (`locations`), is not a coercion failure, and
 * carries `code`, which is the one `extensions.code` each transport gives it.
 */
function isValidationErrorList(errors: unknown, code: string | undefined): boolean {
  if (!Array.isArray(errors) || errors.length === 0) return false;
  return errors.every((error: unknown) => {
    if (typeof error !== "object" || error === null) return false;
    const { message, locations, extensions } = error as {
      message?: unknown;
      locations?: unknown;
      extensions?: { code?: unknown };
    };
    return (
      typeof message === "string" &&
      !VARIABLE_COERCION_MESSAGE.test(message) &&
      Array.isArray(locations) &&
      locations.length > 0 &&
      extensions?.code === code
    );
  });
}

/**
 * True when a `POST /graphql` answer is a schema-validation refusal.
 *
 * Sangria writes that body as `{"errors": [{"message", "locations"}]}`, with no
 * `data` and no `extensions.code`. Each other 400 differs from it:
 * - the transport envelope (`GraphQLTransportError`) carries no `errors` array;
 * - an unknown operation name or a rejected-complexity error carries no
 *   `locations`;
 * - a variable-coercion error matches {@link VARIABLE_COERCION_MESSAGE}.
 */
export function isSchemaMismatchResponse(status: number | undefined, body: unknown): boolean {
  if (status !== 400 || typeof body !== "object" || body === null) return false;
  return isValidationErrorList((body as { errors?: unknown }).errors, undefined);
}

/**
 * True when a subscription's `error` frame is a schema-validation refusal.
 *
 * `GraphQLWebSocketHandler.errorPayload` keeps Sangria's entries, `locations`
 * included, and adds `extensions.code: BAD_USER_INPUT`. A resolver that throws
 * the same code mid-stream gets one entry with no `locations`, so it does not
 * match.
 */
function isSchemaMismatchFrame(errors: unknown): boolean {
  return isValidationErrorList(errors, "BAD_USER_INPUT");
}

/** Refuses every operation once the latch is set, without touching the network. */
const gateLink = new ApolloLink((operation, forward) => {
  if (!schemaMismatchDetected.value) return forward(operation);
  return new Observable((observer) => {
    observer.error(new Error(SCHEMA_MISMATCH_MESSAGE));
  });
});

const detectLink = onError(({ networkError, operation }) => {
  // See `maintenanceLink` in `client.ts` for why `networkError` is the accessor.
  // HTTP fails with a `ServerError` carrying the parsed body as `result`.
  // `GraphQLWsLink` fails with an `ApolloError` carrying the frame's errors as
  // `graphQLErrors`.
  const failure = networkError as
    | { statusCode?: number; result?: { errors?: unknown }; graphQLErrors?: unknown }
    | undefined;
  if (schemaMismatchDetected.value) return;
  const refused =
    isSchemaMismatchResponse(failure?.statusCode, failure?.result) || isSchemaMismatchFrame(failure?.graphQLErrors);
  if (!refused) return;
  logger.warn(`[GraphQL] ${operation.operationName} refused by schema validation; asking for a reload`, {
    errors: failure?.result?.errors ?? failure?.graphQLErrors,
  });
  schemaMismatchDetected.value = true;
});

/** Place before the split, so the gate and the detection cover both transports. */
export const schemaMismatchLink = from([gateLink, detectLink]);
